<script setup lang="ts">
import { computed } from 'vue';
import { vInfiniteScroll } from '@vueuse/components';
import type { FetchTicketResponse } from 'src/api/models';
import useDateTime from 'src/composables/useDateTime';
import TicketPriorityIcon from 'src/components/ticket/TicketPriorityIcon.vue';
import TicketStatusIcon from 'src/components/ticket/TicketStatusIcon.vue';
import { parseUTCDate } from 'src/utils/date-utc';

interface Props {
  filteredUnresolved: FetchTicketResponse[];
  filteredUnplanned: FetchTicketResponse[];
  filteredPending: FetchTicketResponse[];
  filteredScheduled: FetchTicketResponse[];
  isSearching: boolean;
  isLoadingMore: boolean;
  hasMorePages: boolean;
  searchError: string | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'open-ticket': [ticket: FetchTicketResponse];
  'locate-on-board': [ticket: FetchTicketResponse];
  'load-more': [];
  'close': [];
}>();

const { getDateDutchFormat } = useDateTime();

interface ResultGroup {
  key: string;
  label: string;
  icon: string;
  tickets: FetchTicketResponse[];
  badgeClass: string;
}

const groups = computed<ResultGroup[]>(() => {
  const result: ResultGroup[] = [];

  if (props.filteredUnresolved.length > 0) {
    result.push({
      key: 'unresolved',
      label: 'Unresolved',
      icon: 'pi pi-exclamation-triangle',
      tickets: props.filteredUnresolved,
      badgeClass: 'bg-red-500/10 text-red-600 dark:text-red-400',
    });
  }

  if (props.filteredUnplanned.length > 0) {
    result.push({
      key: 'unplanned',
      label: 'Unplanned',
      icon: 'pi pi-calendar-times',
      tickets: props.filteredUnplanned,
      badgeClass: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    });
  }

  if (props.filteredPending.length > 0) {
    result.push({
      key: 'pending',
      label: 'Pending',
      icon: 'pi pi-clock',
      tickets: props.filteredPending,
      badgeClass: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    });
  }

  if (props.filteredScheduled.length > 0) {
    result.push({
      key: 'scheduled',
      label: 'Scheduled',
      icon: 'pi pi-calendar',
      tickets: props.filteredScheduled,
      badgeClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    });
  }

  return result;
});

const totalCount = computed(() =>
  props.filteredUnresolved.length
  + props.filteredUnplanned.length
  + props.filteredPending.length
  + props.filteredScheduled.length
);

const hasResults = computed(() => totalCount.value > 0);

function formatUnitInfo(ticket: FetchTicketResponse): string | null {
  const ud = ticket.unit as Record<string, unknown> | null | undefined;
  if (!ud) return null;
  const parts = [];
  if (ud.fleet_number) parts.push(`#${ud.fleet_number}`);
  if (ud.category_name) parts.push(ud.category_name);
  return parts.length > 0 ? parts.join(' / ') : null;
}

function formatScheduledDate(ticket: FetchTicketResponse): string | null {
  if (!ticket.scheduled_date) return null;
  return getDateDutchFormat(parseUTCDate(ticket.scheduled_date));
}

function handleInfiniteScroll() {
  if (!props.isLoadingMore && props.hasMorePages) {
    emit('load-more');
  }
}
</script>

<template>
  <div
    class="absolute inset-0 z-50 bg-surface-ground/95 backdrop-blur-sm overflow-y-auto"
    v-infinite-scroll="[handleInfiniteScroll, { distance: 200 }]"
    @click.self="emit('close')"
  >
    <div class="max-w-5xl mx-auto py-4 px-6">
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2 text-sm text-600 dark:text-400">
          <i class="pi pi-search" />
          <template v-if="isSearching">
            <i class="pi pi-spin pi-spinner" />
            <span>Searching...</span>
          </template>
          <template v-else-if="searchError">
            <span class="text-red-500">{{ searchError }}</span>
          </template>
          <template v-else>
            <span>{{ totalCount }} {{ totalCount === 1 ? 'result' : 'results' }} found</span>
          </template>
        </div>
        <div class="text-xs text-500 dark:text-400">
          Press <kbd class="px-1.5 py-0.5 rounded bg-surface-200 dark:bg-surface-700 font-mono text-xs">Esc</kbd> to close
        </div>
      </div>

      <template v-if="isSearching && !hasResults">
        <div class="flex flex-col items-center justify-center py-16">
          <i class="pi pi-spin pi-spinner text-3xl text-500 mb-3"></i>
          <span class="text-sm text-500">Searching tickets...</span>
        </div>
      </template>

      <template v-else-if="!hasResults && !isSearching">
        <div class="flex flex-col items-center justify-center py-16">
          <i class="pi pi-search text-3xl text-400 mb-3"></i>
          <span class="text-sm text-500">No tickets found</span>
        </div>
      </template>

      <template v-else>
        <div v-for="group in groups" :key="group.key" class="mb-4">
          <div class="flex items-center gap-2 mb-2 px-1">
            <i :class="[group.icon, 'text-sm text-500']" />
            <span class="text-sm font-semibold text-700 dark:text-300">{{ group.label }}</span>
            <span
              class="text-xs px-2 py-0.5 rounded-full font-medium"
              :class="group.badgeClass"
            >
              {{ group.tickets.length }}
            </span>
          </div>

          <div class="flex flex-col gap-1">
            <div
              v-for="ticket in group.tickets"
              :key="ticket.id"
              class="flex items-center gap-3 px-3 py-2 rounded-lg bg-surface-0 dark:bg-surface-800 border border-surface-200 dark:border-surface-700 cursor-pointer hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors group"
              @click="emit('open-ticket', ticket)"
            >
              <span class="font-mono text-sm font-medium text-900 dark:text-100 w-16 flex-shrink-0">
                #{{ ticket.number }}
              </span>

              <TicketPriorityIcon :priority="ticket.priority" class="flex-shrink-0" />

              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-900 dark:text-100 truncate">
                  {{ ticket.title }}
                </div>
              </div>

              <TicketStatusIcon v-if="ticket.status" :status="ticket.status" class="flex-shrink-0" />

              <div v-if="ticket.assignee?.name" class="flex items-center gap-1 text-xs text-600 dark:text-400 flex-shrink-0 max-w-[120px]">
                <i class="pi pi-user" style="font-size: 0.65rem" />
                <span class="truncate">{{ ticket.assignee.name }}</span>
              </div>

              <div v-if="formatUnitInfo(ticket)" class="flex items-center gap-1 text-xs text-600 dark:text-400 flex-shrink-0 max-w-[140px]">
                <i class="pi pi-truck" style="font-size: 0.65rem" />
                <span class="truncate">{{ formatUnitInfo(ticket) }}</span>
              </div>

              <div v-if="formatScheduledDate(ticket)" class="text-xs text-500 dark:text-400 flex-shrink-0 w-20 text-right">
                {{ formatScheduledDate(ticket) }}
              </div>

              <button
                v-if="ticket.scheduled_date"
                type="button"
                class="flex-shrink-0 p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Locate on board"
                @click.stop="emit('locate-on-board', ticket)"
              >
                <i class="pi pi-map-marker text-primary-500" style="font-size: 0.8rem" />
              </button>
            </div>
          </div>
        </div>

        <div v-if="isLoadingMore" class="flex items-center justify-center py-4 gap-2">
          <i class="pi pi-spin pi-spinner text-sm text-primary-500"></i>
          <span class="text-xs text-600 dark:text-400">Loading more results...</span>
        </div>
      </template>
    </div>
  </div>
</template>
